import type { FieldValues, Path } from 'react-hook-form';
import { formatPageUpdateValue } from '../components/PageUpdateApprovalCard';
import { FORM_UI_PREVIEW_VALUES } from './constants';
import type {
  FormUiField,
  FormUiFieldOption,
  FormUiFields,
  FormUiUserOnlyStatus,
} from './types';
import type { PageUpdatePreviewRow } from './uiContext';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const parseJsonIfString = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const isEmptyFormValue = (value: unknown) =>
  value == null ||
  (typeof value === 'string' && value.trim() === '') ||
  (typeof value === 'number' && Number.isNaN(value)) ||
  (Array.isArray(value) && value.length === 0);

const isFormUiFieldName = <TForm extends FieldValues>(
  fields: FormUiFields<TForm>,
  name: string,
): name is Path<TForm> => Object.prototype.hasOwnProperty.call(fields, name);

export const getFormUiFieldEntries = <TForm extends FieldValues>(
  fields: FormUiFields<TForm>,
) =>
  Object.keys(fields).flatMap<[Path<TForm>, FormUiField<TForm>]>((name) => {
    if (!isFormUiFieldName(fields, name)) {
      return [];
    }
    const field = fields[name];
    return field ? [[name, field]] : [];
  });

export const findFormUiField = <TForm extends FieldValues>(
  fields: FormUiFields<TForm>,
  name: string,
): FormUiField<TForm> | undefined =>
  isFormUiFieldName(fields, name) ? fields[name] : undefined;

export const isFormUiFieldVisible = <TForm extends FieldValues>(
  field: FormUiField<TForm>,
  values: Record<string, unknown>,
) => field.isVisible?.(values) ?? true;

export const getUserOnlyStatus = <TForm extends FieldValues>(
  field: FormUiField<TForm>,
  value: unknown,
): FormUiUserOnlyStatus => {
  if (field.isSaved?.(value)) {
    return 'saved';
  }
  return isEmptyFormValue(value) ? 'empty' : 'entered';
};

export const isMissingRequiredValue = <TForm extends FieldValues>(
  field: FormUiField<TForm>,
  value: unknown,
) =>
  !!field.required &&
  (field.secret
    ? getUserOnlyStatus(field, value) === 'empty'
    : isEmptyFormValue(value));

export const getInvalidOptionReason = (
  options: FormUiFieldOption[] | undefined,
  value: unknown,
): string | undefined => {
  if (!options || options.some((option) => option.value === value)) {
    return undefined;
  }
  const allowed = options
    .map((option) => (option.value === null ? 'null' : option.value))
    .join(', ');
  return `Must be one of: ${allowed}`;
};

export const describeFormUiField = <TForm extends FieldValues>(
  name: string,
  field: FormUiField<TForm>,
  value: unknown,
) => ({
  name,
  label: field.label,
  ...(field.description ? { description: field.description } : {}),
  ...(field.required ? { required: true } : {}),
  ...(field.options ? { options: field.options } : {}),
  ...(field.secret
    ? { userOnly: true, status: getUserOnlyStatus(field, value) }
    : {}),
});

const readErrorMessage = (value: unknown): string | undefined =>
  isRecord(value) && typeof value.message === 'string'
    ? value.message
    : undefined;

export const flattenFormErrors = (
  errors: unknown,
  path = '',
): Record<string, string> => {
  const message = readErrorMessage(errors);
  if (message !== undefined) {
    return path ? { [path]: message } : {};
  }
  if (!errors || typeof errors !== 'object') {
    return {};
  }
  return Object.entries(errors).reduce<Record<string, string>>(
    (flattened, [key, value]) =>
      key === 'ref'
        ? flattened
        : {
            ...flattened,
            ...flattenFormErrors(value, path ? `${path}.${key}` : key),
          },
    {},
  );
};

const formatFormUiValue = <TForm extends FieldValues>(
  field: FormUiField<TForm>,
  value: unknown,
) =>
  field.options?.find((option) => option.value === value)?.label ??
  formatPageUpdateValue(value) ??
  FORM_UI_PREVIEW_VALUES.empty;

export const previewFormUiUpdate = <TForm extends FieldValues>(
  fields: FormUiFields<TForm>,
  proposed: Record<string, unknown>,
): PageUpdatePreviewRow[] =>
  Object.entries(proposed).flatMap(([name, value]) => {
    const field = findFormUiField(fields, name);
    if (!field) {
      return [
        {
          field: name,
          label: name,
          value: FORM_UI_PREVIEW_VALUES.unknownField,
        },
      ];
    }
    if (field.secret) {
      return [
        {
          field: name,
          label: field.label,
          value: FORM_UI_PREVIEW_VALUES.userOnlyField,
        },
      ];
    }
    if (field.preview) {
      return field.preview(value);
    }
    return [
      {
        field: name,
        label: field.label,
        value: formatFormUiValue(field, value),
      },
    ];
  });

export const getFormUiReadDescription = (name: string, prompt: string) =>
  `${prompt} read_${name} returns form (the live unsaved values of the visible fields; values of user-only fields are never returned), fields (each visible field with name, label, an optional description, required, options with the value to write, the label the form shows and an optional note such as Recommended or Deprecated, and for user-only fields such as passwords, keys, tokens and credential files: userOnly with status empty, entered or saved), missingRequiredFields, validationErrors, and the page state described above. Not available unless the page is open.`;

export const getFormUiWriteDescription = (name: string, prompt: string) =>
  `${prompt} write_${name} proposes values for the visible fields: form is an object of values keyed by the field names from read_${name}, written as the form holds them. Fields revealed by the same write (for example a type together with the fields of that type) can be set in one call. Select fields only accept an option value; refer to options by their label when talking to the user, and suggest the Recommended option when the user has no preference. User-only fields cannot be written: tell the user which ones to fill in directly in the form, and never ask them to paste secrets into the chat. The form validates the values, and validationErrors explains what to fix. Values the page fills in by itself after a write, such as a default that follows another field, only appear on the next read_${name}, so read again before telling the user what is still missing. Does not submit the form. Requires user approval.`;
